import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * A custom element that renders a video background.
 *
 * The video sources are deferred until after the first paint so the poster image
 * remains the LCP element and the video does not compete for bandwidth or the main
 * thread during the LCP window. Autoplay still starts shortly after the initial paint.
 * When the user prefers reduced motion the video is never loaded and the poster image
 * is shown instead.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {boolean} */
  #loaded = false;

  connectedCallback() {
    super.connectedCallback();

    // Respect reduced-motion: leave the poster image in place and never load the video.
    if (prefersReducedMotion()) return;

    // Defer loading until after the first paint so the poster image wins LCP and the
    // video stays out of the critical path.
    requestAnimationFrame(() => this.#whenIdle(() => this.#loadVideo()));
  }

  /**
   * Runs the callback when the main thread is idle, falling back to a short timeout.
   * @param {() => void} callback
   */
  #whenIdle(callback) {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(callback, { timeout: 2000 });
    } else {
      setTimeout(callback, 200);
    }
  }

  #loadVideo() {
    if (this.#loaded || !this.isConnected) return;
    this.#loaded = true;

    const { videoSources, videoElement } = this.refs;

    if (!videoElement) return;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource && !source.getAttribute('src')) source.setAttribute('src', videoSource);
    }

    videoElement.load();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
